import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ContentPageTheme, Form, ReviewPageConfig, ReviewPageConfigServices } from "@/services/bedrock";
import { LiquidPlaceholderGroups, groupLiquidPlaceholders } from "@/utils/liquidHelpers";
import { ConfigSkeleton } from "../components/LoadingStates";
import { ShowcaseLayout } from "../components/ShowcaseLayout";
import { PreviewPlayerState } from "../components/usePreviewPlayer";
import { FullReviewsPageConfigForm } from "./components/FullReviewsPageConfigForm";
import { FullReviewsPagePreview } from "./components/FullReviewsPagePreview";
import { useFullReviewsPagePreview } from "./components/useFullReviewsPagePreview";

interface ViewProps {
  themes: ContentPageTheme[];
  forms: Form[];
  preview: PreviewPlayerState;
  onHasChangesChange: (hasChanges: boolean) => void;
}

interface ContentProps extends ViewProps {
  config: ReviewPageConfig;
  updateConfig: (updates: Partial<ReviewPageConfig>) => void;
  placeholders: LiquidPlaceholderGroups;
}

function FullReviewsPageContent({
  config,
  updateConfig,
  themes,
  forms,
  preview,
  placeholders,
  onHasChangesChange,
}: ContentProps) {
  const previewProps = useFullReviewsPagePreview({
    config,
    themes,
    previewPlayer: preview.previewPlayer,
  });

  return (
    <ShowcaseLayout
      settings={
        <FullReviewsPageConfigForm
          config={config}
          updateConfig={updateConfig}
          themes={themes}
          forms={forms}
          placeholders={placeholders}
          onHasChangesChange={onHasChangesChange}
        />
      }
      preview={<FullReviewsPagePreview preview={preview} {...previewProps} />}
    />
  );
}

export function FullReviewsPage({ themes, forms, preview, onHasChangesChange }: ViewProps) {
  const [config, setConfig] = useState<ReviewPageConfig | null>(null);

  const { data: configData, isError } = useQuery({
    queryKey: ["reviewPageConfig"],
    queryFn: () => ReviewPageConfigServices.getConfig(),
    gcTime: 0,
  });

  const { data: liquidDrop } = useQuery({
    queryKey: ["reviewPageConfigLiquidVariables"],
    queryFn: () => ReviewPageConfigServices.getLiquidVariables(),
  });

  useEffect(() => {
    if (configData) setConfig(current => current ?? configData);
  }, [configData]);

  useEffect(() => {
    if (isError) toast.error("Error loading Full Reviews Page settings");
  }, [isError]);

  const placeholders = useMemo(() => groupLiquidPlaceholders(liquidDrop?.liquidVariables ?? []), [liquidDrop]);

  const updateConfig = (updates: Partial<ReviewPageConfig>) => {
    setConfig(current => (current ? { ...current, ...updates } : current));
  };

  if (!config) {
    return <ConfigSkeleton />;
  }

  return (
    <FullReviewsPageContent
      config={config}
      updateConfig={updateConfig}
      themes={themes}
      forms={forms}
      preview={preview}
      placeholders={placeholders}
      onHasChangesChange={onHasChangesChange}
    />
  );
}
